/**
 * Content Filter Matching Engine
 * 
 * Normalizes text and matches against prohibited word lists.
 * Handles common evasion techniques: leetspeak, repeated chars, diacritics.
 */

// Leetspeak / character substitution map
const LEET_MAP = {
  '@': 'a',
  '4': 'a',
  '8': 'b',
  '(': 'c',
  '3': 'e',
  '6': 'g',
  '#': 'h',
  '!': 'i',
  '1': 'i',
  '|': 'i',
  '0': 'o',
  '5': 's',
  '$': 's',
  '7': 't',
  '+': 't',
  '%': 'x',
  '2': 'z',
};

/**
 * Normalize text for matching:
 * 1. Lowercase
 * 2. Strip diacritics/accents (é → e, ñ → n)
 * 3. Convert leetspeak substitutions
 * 4. Collapse repeated characters (heeello → hello)
 * 5. Collapse multiple spaces
 */
export function normalizeText(text) {
  if (!text) return '';

  let normalized = text
    .toLowerCase()
    // Strip diacritics
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

  // Convert leetspeak characters
  normalized = normalized
    .split('')
    .map(char => LEET_MAP[char] || char)
    .join('');

  // Collapse repeated characters (3+ of the same letter → 1)
  // e.g., "heeello" → "helo", "niggger" → "niger"
  normalized = normalized.replace(/(.)\1{2,}/g, '$1');

  // Collapse multiple spaces/whitespace
  normalized = normalized.replace(/\s+/g, ' ').trim();

  return normalized;
}

/**
 * Escape a string for use in a regex pattern
 */
function escapeRegex(str) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Build a regex for a prohibited phrase with word boundary matching.
 * Multi-word phrases use flexible whitespace matching.
 */
function buildWordPattern(phrase) {
  const normalizedPhrase = normalizeText(phrase);
  const words = normalizedPhrase.split(/\s+/);

  if (words.length > 1) {
    // Multi-word phrase: match with flexible whitespace between words
    const pattern = words.map(w => escapeRegex(w)).join('\\s+');
    return new RegExp(`(?:^|\\b|\\s)${pattern}(?:\\b|\\s|$)`, 'i');
  }

  // Single word: use word boundaries
  return new RegExp(`\\b${escapeRegex(normalizedPhrase)}\\b`, 'i');
}

/**
 * Check text against a single category's word list.
 * Returns array of matched terms.
 */
function matchCategory(normalizedText, words) {
  const matches = [];

  for (const word of words) {
    const pattern = buildWordPattern(word);
    if (pattern.test(normalizedText)) {
      matches.push(word);
    }
  }

  return matches;
}

/**
 * Match normalized text against all provided categories.
 * 
 * @param {string} normalizedText - Already normalized text
 * @param {Object} categories - Categories object from prohibitedWords.js
 * @returns {Array<{category: string, label: string, severity: string, matchedTerms: string[]}>}
 */
export function findViolations(normalizedText, categories) {
  const violations = [];

  for (const [categoryKey, categoryData] of Object.entries(categories)) {
    const matches = matchCategory(normalizedText, categoryData.words);

    if (matches.length > 0) {
      violations.push({
        category: categoryKey,
        label: categoryData.label,
        severity: categoryData.severity,
        matchedTerms: matches,
      });
    }
  }

  return violations;
}
